import * as React from "react";
import { Block } from "kanopi-components/Block";
import { Card, CardBody } from "kanopi-components/Card";
import { expandBorder } from "kanopi-components/utils/styles";
import { frequencyToString } from "kw/util/utils";
import { KIND } from "baseui/button";
import { NavButton } from "kw/components/NavButton/NavButton";
import { navigate } from "@reach/router";
import { PolicyStatus } from "kw/api/zeus";
import { PolicyStatusBanner } from "kanopi-components/Banner/PolicyStatusBanner";
import { useStyletron } from "kanopi-components/theme/useStyletron";
import {
  HeadingMedium,
  ParagraphMedium,
  ParagraphXSmall,
} from "kanopi-components/typography";
import { useFormatter } from "kw/util/useFormatter";

export type TPolicySummaryCardBuilder = {
  ctaTitle?: string;
  ctaCallback?: (policy?: TPolicy) => void | unknown;
  ctaKind?: KIND[keyof KIND];
};

type TPolicySummaryCardProps = {
  policy: TPolicy;
  splitCard?: boolean;
  backgroundColor?: string;
} & TPolicySummaryCardBuilder;

export const PolicySummaryCard: React.FC<TPolicySummaryCardProps> = ({
  policy,
  splitCard = false,
  ctaTitle = "View details",
  ctaCallback = policyDetailsRedirector,
  backgroundColor,
  ctaKind = KIND.primary,
}) => {
  const [, theme] = useStyletron();

  const headerContent = (
    <>
      <HeadingMedium marginTop="scale200" marginBottom={0} center>
        {policy?.quote?.product?.name}
      </HeadingMedium>
      <ParagraphXSmall center marginTop={0}>
        {policy?.quote?.userReference}
      </ParagraphXSmall>
    </>
  );

  const footerContent = (
    <>
      <NavButton
        kind={ctaKind}
        onClick={() => {
          ctaCallback(policy);
        }}
        data-testid={`policyCta-${policy.id}`}
      >
        {ctaTitle}
      </NavButton>
    </>
  );

  const cardLayout = (
    <>
      <CardBody
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        height={`calc(100% - (${theme.sizing.scale600} *2))`}
      >
        <Block minHeight="5.25rem">{headerContent}</Block>
        <PolicyCardContent policy={policy} />
        <PolicyStatusBanner
          marginTop={theme.sizing.scale500}
          policyId={policy.id}
          policyLatestUpdate={policy?.latestUpdate}
        />
        {ctaTitle && <Block marginTop="scale800">{footerContent}</Block>}
      </CardBody>
    </>
  );

  const splitLayout = (
    <>
      <CardBody
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        gridRowGap={theme.sizing.scale100}
      >
        <Block
          padding={`${theme.sizing.scale500} ${theme.sizing.scale750}`}
          display="flex"
          flexDirection="column"
          minHeight="5.25rem"
          backgroundColor={theme.colors.backgroundTertiary}
          borderRadius={theme.borders.radius200}
          gridRowGap={theme.sizing.scale500}
          alignItems="center"
        >
          {headerContent}
        </Block>
        <Block
          padding={`${theme.sizing.scale500} ${theme.sizing.scale750}`}
          display="flex"
          flexDirection="column"
          gridRowGap={theme.sizing.scale500}
          backgroundColor={theme.colors.backgroundTertiary}
          borderRadius={theme.borders.radius200}
        >
          <PolicyCardContent policy={policy} labelColor={theme.colors.black} />

          {ctaTitle && (
            <Block display="flex" flexDirection="column">
              {footerContent}
            </Block>
          )}
        </Block>
      </CardBody>
    </>
  );

  return (
    <Card
      backgroundColor={
        splitCard ? "inherit" : backgroundColor || theme.colors.backgroundPrimary
      }
      overrides={{
        Block: {
          props: {
            "data-testid": `PolicySummaryCard-${policy.id}`,
          },
          style: {
            ...expandBorder("0px", "Width"),
          },
        },
      }}
    >
      {splitCard ? splitLayout : cardLayout}
    </Card>
  );
};

type TPolicyCardContent = {
  policy: TPolicy;
  labelColor?: string;
};
const PolicyCardContent: React.FC<TPolicyCardContent> = ({ policy, labelColor }) => {
  const [css, theme] = useStyletron();
  const items = getPolicyDetails(policy);
  return (
    <table data-testid={"policy-details"}>
      <tbody>
        {Object.keys(items).map((label, index) => (
          <tr key={index}>
            <td
              width="50%"
              className={css({
                paddingRight: theme.sizing.scale400,
                paddingBottom: theme.sizing.scale100,
                textAlign: "right",
              })}
            >
              <ParagraphMedium
                color={labelColor || theme.colors.contentTertiary}
                margin={0}
              >
                {label}
              </ParagraphMedium>
            </td>
            <td
              className={css({
                paddingLeft: theme.sizing.scale400,
              })}
            >
              <ParagraphMedium margin={0}>{items[label]}</ParagraphMedium>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
const getPolicyDetails = (policy: TPolicy) => {
  const { formatCurrency, formatDate } = useFormatter();

  const paymentFrequency = frequencyToString(policy.quote.paymentFrequency);
  const details = {
    "Policy number": policy.policyNumber,
    Premium: `${formatCurrency(
      policy.quote.periodicPrice
    )} ${paymentFrequency.toLowerCase()}`,
  };

  if (policy.autoRenewEnabled && policy.status !== PolicyStatus.CANCELLED) {
    details["Renews"] = formatDate(policy?.finishDate) || "-";
    details["Next payment"] =
      policy?.latestRenewal?.quote?.riskProfile?.periodicPrice || "-";
  }

  if (policy.status === PolicyStatus.CANCELLED) {
    details["Cancelled"] = new Date(policy.finishDate).toLocaleDateString();
  } else if (!policy.autoRenewEnabled) {
    details["Expires"] = new Date(policy.finishDate).toLocaleDateString();
  }

  return details;
};

const policyDetailsRedirector = (policy) => {
  navigate(`/policies/${policy.id}/`);
};
